import type { components } from '@/lib/api/generated/schema'

export type Schemas = components['schemas']
export type Activity = Schemas['ActivityProjection']
export type SavedActivity = Schemas['SavedActivity']
export type Category = Schemas['Category']
export type Friend = Schemas['Friend']
export type ParticipationMode = Schemas['ParticipationMode']

/**
 * A selected discovery center. `city` is `null` when reverse geocoding did not
 * return a name, so the UI can fall back without inventing one. Coordinates
 * are used for the radius filter and are rounded before storage.
 *
 * An explicitly selected city lives in the filter store and always wins over
 * the ambient device fix. GPS-derived city names may now be persisted as the
 * per-user ambient location and written to the profile (best effort); this
 * does not change the explicit city filter behavior.
 */
export interface DiscoveryLocation {
  city: string | null
  latitude: number
  longitude: number
}

export type DiscoveryView = 'list' | 'map'

export interface MapBounds {
  south: number
  north: number
  west: number
  east: number
}

/**
 * `SavedActivity` is the saved-activities projection: it lacks
 * `joinWindowClosed`, which the discovery card derives from `endAt`.
 */
export function savedToActivity(item: SavedActivity): Activity {
  const end = item.endAt ? Date.parse(item.endAt) : Number.NaN
  // The frozen SavedActivity schema omits coordinates; the saved-only view
  // intersects with the regular list, so missing coordinates are expected and
  // simply keep the card out of the map pins.
  const withCoordinates = item as SavedActivity & {
    latitude?: number | null
    longitude?: number | null
  }
  return {
    ...item,
    latitude: withCoordinates.latitude ?? null,
    longitude: withCoordinates.longitude ?? null,
    joinWindowClosed: Number.isFinite(end)
      ? end - Date.now() <= 60 * 60 * 1000
      : false,
  }
}

export function activityCoordinates(
  activity: Pick<Activity, 'latitude' | 'longitude'>,
): { latitude: number; longitude: number } | null {
  if (activity.latitude === null || activity.longitude === null) return null
  if (!Number.isFinite(activity.latitude) || !Number.isFinite(activity.longitude)) {
    return null
  }
  return { latitude: activity.latitude, longitude: activity.longitude }
}
